import { Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TextBadge } from '@/components/StatusBadge';
import type { DataColumn } from '@/components/DataTable';
import { CardNo, MemberLink, RecordLink } from '@/components/links/RecordLinks';
import type { BalanceListItem } from '@/types';
import { formatCurrency, formatPaymentDate } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';
import { describeBalanceActivity } from '@/lib/balances';
import { displayMemberNo } from '@/lib/utils';
import { ICON_BUTTON_CLASS } from '@/lib/styles';

export interface BalanceRowActions {
  onOpenBalance: (item: BalanceListItem) => void;
}

function LastActivity({ item }: { item: BalanceListItem }) {
  const activity = item.lastActivity;
  if (!activity) return <span className="text-gray-400">--</span>;
  return (
    <div>
      <p className="tabular-nums text-gray-700">{formatPaymentDate(activity.date)}</p>
      <p className="text-xs text-gray-500">
        {describeBalanceActivity(activity)}
        {activity.refNo && (
          <>
            {' · '}
            <RecordLink member={{ id: item.memberId, name: item.memberName, memberNo: item.memberNo }} refNo={activity.refNo} />
          </>
        )}
      </p>
    </div>
  );
}

export const createColumns = ({ onOpenBalance }: BalanceRowActions): DataColumn<BalanceListItem>[] => [
  {
    id: 'id',
    header: 'ID',
    className: 'w-[64px]',
    render: (item) => <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 font-medium">{displayMemberNo({ id: item.memberId, memberNo: item.memberNo })}</span>,
  },
  { id: 'cardNo', header: 'Card No', render: (item) => <CardNo value={item.cardNo} /> },
  {
    id: 'member',
    header: 'Member',
    render: (item) => (
      <div>
        <MemberLink memberId={item.memberId} memberNo={item.memberNo} name={item.memberName} className="block font-medium text-gray-900" />
        <span className="text-xs text-gray-500">{item.memberPhone}</span>
      </div>
    ),
  },
  {
    id: 'added',
    header: 'Paid by PMS',
    render: (item) => <span className="font-semibold tabular-nums">{formatCurrency(item.added)}</span>,
  },
  { id: 'interest', header: 'Interest Charged', render: (item) => <span className="tabular-nums">{formatCurrency(item.interestCharged)}</span> },
  {
    id: 'repaid',
    header: 'Repaid',
    render: (item) =>
      item.repaid ? <span className="tabular-nums text-teal-800">{formatCurrency(item.repaid)}</span> : <span className="text-gray-400">--</span>,
  },
  {
    id: 'balanceDue',
    header: 'Balance Due',
    render: (item) =>
      item.balanceDue > 0 ? (
        <div>
          <button type="button" onClick={() => onOpenBalance(item)} className="font-semibold tabular-nums text-amber-900 hover:underline">
            {formatCurrency(item.balanceDue)}
          </button>
          <p className="whitespace-nowrap text-xs text-gray-500">
            {item.interestDue ? `Incl. ${formatCurrency(item.interestDue)} interest` : 'No interest yet'}
          </p>
        </div>
      ) : (
        <span className="text-gray-400">--</span>
      ),
  },
  { id: 'since', header: 'Since', render: (item) => <span className="text-gray-700">{formatMonthKey(item.since)}</span> },
  { id: 'lastActivity', header: 'Last Activity', render: (item) => <LastActivity item={item} /> },
  {
    id: 'status',
    header: 'Status',
    render: (item) => <TextBadge tone={item.balanceDue > 0 ? 'amber' : 'teal'}>{item.balanceDue > 0 ? 'Balance Due' : 'Cleared'}</TextBadge>,
  },
  {
    id: 'actions',
    header: 'Actions',
    className: 'text-center',
    render: (item) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={() => onOpenBalance(item)} className={ICON_BUTTON_CLASS}>
            <Coins className="h-3.5 w-3.5" />
            <span className="sr-only">Open Balance</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Open Balance</TooltipContent>
      </Tooltip>
    ),
  },
];
